import {
  ComponentProps,
  MouseEvent,
  ReactNode,
  FunctionComponent,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'src/utils/twMerge';

import Button from 'src/designSystem/Button';
import ButtonStack from 'src/designSystem/ButtonStack';
import CloseButton from 'src/designSystem/CloseButton';
import Overlay from 'src/designSystem/Overlay';
import PageHeader, {
  PAGE_X,
} from 'src/designSystem/PageHeader';
import SunburstRays, {
  SunburstSkin,
} from 'src/designSystem/SunburstRays';
import { Style } from 'src/designSystem/StatusBar';
import PageLayout from 'src/layouts/PageLayout';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { hapticNotification, NotificationType } from 'src/utils/haptics';

export const CELEBRATION_TRANSITION_MS = 200;

export type CelebrationEnter = 'fade' | 'slide-from-right' | 'none';
export type CelebrationContainer = 'body' | 'page';

export type CelebrationSkin = {
  background?: string;
  rays?: SunburstSkin;
  button?: ComponentProps<typeof Button>['type'];
  isOnLight?: boolean;
};

export const BACKDROP_SKIN: CelebrationSkin = {};

type CelebrationTestIds = {
  root?: string;
  rays?: string;
  raysDisc?: string;
  continueButton?: string;
  closeButton?: string;
};

type DismissMethod = 'close_button' | 'continue_button' | 'backdrop';

type Props = {
  analyticsName: string;
  onContinue: () => void;
  skin?: CelebrationSkin;
  testIds?: CelebrationTestIds;
  isOpen?: boolean;
  continueLabel?: ReactNode;
  footer?: ReactNode;
  headerContent?: ReactNode;
  decoration?: ReactNode;
  hasCloseButton?: boolean;
  isBackdropDismissible?: boolean;
  isFooterHidden?: boolean;
  isFooterInvisible?: boolean;
  className?: string;
  children: ReactNode;
  enter?: CelebrationEnter;
  container?: CelebrationContainer;
  isHeld?: boolean;
  isHapticOnView?: boolean;
};

const ENTER_CLASS: Record<CelebrationEnter, string | undefined> = {
  fade: 'slideUp',
  'slide-from-right': 'celebration-enter-right',
  none: undefined,
};

const heldId = (id: string | undefined, suffix: string) =>
  id === undefined ? undefined : `${id}${suffix}`;

const CelebrationModal: FunctionComponent<Props> = ({
  analyticsName,
  onContinue,
  skin = BACKDROP_SKIN,
  testIds = {},
  isOpen = true,
  continueLabel,
  footer,
  headerContent,
  decoration,
  hasCloseButton = true,
  isBackdropDismissible = true,
  isFooterHidden = false,
  isFooterInvisible = false,
  className,
  children,
  enter = 'fade',
  container = 'body',
  isHeld = false,
  isHapticOnView = false,
}) => {
  const hasLoggedViewRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasLoggedViewRef.current = false;
      return;
    }
    if (isHeld || hasLoggedViewRef.current) return;
    hasLoggedViewRef.current = true;
    void logAnalyticsEvent('celebration_viewed', {
      celebration_name: analyticsName,
    });
    if (isHapticOnView) void hapticNotification(NotificationType.Success);
  }, [isOpen, isHeld, analyticsName, isHapticOnView]);

  if (!isOpen) return null;

  const dismiss = (method: DismissMethod) => {
    if (isHeld) return;
    void logAnalyticsEvent('celebration_closed', {
      celebration_name: analyticsName,
      dismiss_method: method,
    });
    onContinue();
  };
  const dismissFrom =
    (method: DismissMethod) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      dismiss(method);
    };
  const suffix = isHeld ? '-held' : '';
  const hasFooter =
    !isFooterHidden && (footer !== undefined || continueLabel !== undefined);

  const root = (
    <Overlay
      onClick={isBackdropDismissible ? () => dismiss('backdrop') : undefined}
      data-testid={heldId(testIds.root, suffix)}
      aria-hidden={isHeld ? true : undefined}
      className={twMerge(
        container === 'page' ? 'fixed' : 'absolute',
        'z-celebration',
        ENTER_CLASS[enter],
        isHeld && 'pointer-events-none',
        skin.background && 'bg-transparent',
        className,
      )}
    >
      <div
        className={twMerge(
          container === 'page'
            ? 'absolute inset-0 mx-auto w-full max-w-app-column'
            : 'absolute inset-y-0 fixed-in-column',
          (skin.background || skin.rays) && 'overflow-hidden',
          skin.background,
        )}
      >
        {skin.rays && (
          <div
            data-testid={testIds.rays}
            className="pointer-events-none absolute inset-x-0 top-0 flex h-[94%] items-center justify-center"
          >
            <SunburstRays
              skin={skin.rays}
              isSpinning
              data-testid={testIds.raysDisc}
              className="h-[150vmax] w-[150vmax] shrink-0 rounded-full"
            />
          </div>
        )}
        {decoration}
        <div className="relative h-full">
          <PageLayout
            fitContent
            appBackground=""
            statusBar={{
              style: skin.isOnLight ? Style.Light : Style.Dark,
              isTransient: true,
            }}
            header={
              <PageHeader
                isOnDark={!skin.isOnLight}
                data-testid={`celebration-header${suffix}`}
                left={
                  hasCloseButton && (
                    <CloseButton
                      onClick={dismissFrom('close_button')}
                      isOnDark={!skin.isOnLight}
                      isDisabled={isHeld}
                      data-testid={heldId(testIds.closeButton, suffix)}
                    />
                  )
                }
              >
                {headerContent}
              </PageHeader>
            }
            footer={
              hasFooter && (
                <div
                  aria-hidden={isFooterInvisible || undefined}
                  inert={isFooterInvisible}
                  role="presentation"
                  onClick={event => event.stopPropagation()}
                  className={twMerge(
                    'pb-4 transition-[opacity,visibility]',
                    PAGE_X,
                    isFooterInvisible && 'invisible opacity-0',
                  )}
                >
                  <ButtonStack>
                    {footer}
                    {continueLabel !== undefined && (
                      <Button
                        data-testid={heldId(testIds.continueButton, suffix)}
                        type={skin.button}
                        onClick={dismissFrom('continue_button')}
                        className="relative w-full"
                      >
                        {continueLabel}
                      </Button>
                    )}
                  </ButtonStack>
                </div>
              )
            }
          >
            <div
              className={twMerge(
                'flex min-h-0 flex-1 flex-col justify-center',
                PAGE_X,
              )}
            >
              {children}
            </div>
          </PageLayout>
        </div>
      </div>
    </Overlay>
  );

  return container === 'page' ? root : createPortal(root, document.body);
};

export default CelebrationModal;
